// Type-only, so it is erased. A value import would pull the SDK back into
// `_app` — see the library README, "Rules for changing this library".
import type { datadogRum } from '@datadog/browser-rum-slim';

import { getWindow, IPublicConfig } from '@r-client/shared/util/core';

import { log } from './log';
import { readCachedAnalyticsConsent } from './osano/analytics-consent-cache';
import { readAnalyticsConsent } from './osano/subscribe-to-analytics-consent';
import { stripUrlQuery } from './sanitize-url';
import { TTrackingConsent } from './types';

/**
 * Must match `DD_SERVICE` on the Helm deployment so browser RUM and the
 * `dd-trace` server APM share one service in the Datadog UI.
 */
export const DATADOG_RUM_SERVICE = 'r-client';

export type TRumSdk = typeof datadogRum;

/**
 * The single source of truth for "is RUM running yet". Caching the promise
 * rather than a boolean is what makes init idempotent across the async load:
 * it is assigned before the import is awaited, so a second synchronous call
 * returns the same promise instead of starting a second download.
 *
 * Deliberately not the SDK's `getInternalContext()` probe — that returns
 * `undefined` for a *sampled-out* session too, so below 100% sampling it would
 * report "not initialised" forever.
 */
let rumReady: Promise<TRumSdk | undefined> | undefined;

/**
 * Resolves to the running SDK, or `undefined` when RUM is disabled, lacks
 * credentials, or failed to load. Callers must treat `undefined` as normal.
 */
export const whenRumReady = (): Promise<TRumSdk | undefined> =>
  rumReady ?? Promise.resolve(undefined);

/** Test-only: drop the cached promise between cases. */
export const resetRumForTests = (): void => {
  rumReady = undefined;
};

/**
 * Starts Datadog Browser RUM, loading the SDK in its own chunk — see ADR
 * 20261015, "Load behaviour".
 *
 * Call once at module scope so the chunk request starts as early as possible.
 * Never throws: RUM is strictly observability and must not break page load.
 */
export const initRum = (
  config: IPublicConfig
): Promise<TRumSdk | undefined> => {
  if (rumReady) {
    return rumReady;
  }

  const { rumApplicationId: applicationId, rumClientToken: clientToken } =
    config;

  // Runs during SSR too, since callers invoke this at module scope.
  const isStartable =
    !!getWindow() && !!config.rumEnabled && !!applicationId && !!clientToken;

  if (!isStartable) {
    if (getWindow() && !config.rumEnabled) {
      log('disabled by config');
    } else if (getWindow()) {
      log('missing applicationId/clientToken — skipping init');
    }

    rumReady = Promise.resolve(undefined);
    return rumReady;
  }

  rumReady = import('@datadog/browser-rum-slim')
    .then(({ datadogRum }) => {
      // Read immediately before `init`, so a refusal that arrived during the
      // download is not missed. Usually the saved copy: Osano is rarely loaded
      // yet. See ADR 20261015, "Privacy position".
      const isAnalyticsGranted =
        readAnalyticsConsent() ?? readCachedAnalyticsConsent();
      const trackingConsent: TTrackingConsent =
        isAnalyticsGranted === false ? 'not-granted' : 'granted';

      datadogRum.init({
        applicationId,
        clientToken,
        site: 'datadoghq.com',
        service: DATADOG_RUM_SERVICE,
        env: config.applicationEnvironment,
        version: config.applicationVersion,
        sessionSampleRate: config.rumSessionSampleRate ?? 0,
        // No-op in the slim build. Explicit so swapping to the full package can
        // never silently enable replay.
        sessionReplaySampleRate: 0,
        // Vitals-only scope; INP is unaffected. See ADR 20261015.
        trackResources: false,
        trackLongTasks: false,
        trackUserInteractions: false,
        // We want a view created automatically per History API navigation; the
        // view is then renamed to the Next route template by useRumViewName.
        trackViewsManually: false,
        // Without this, bfcache restores produce no view at all, undercounting
        // back/forward navigation on mobile Safari and Chrome.
        trackBfcacheViews: true,
        // No durable cross-session id: we collect before the banner is answered.
        // See ADR 20261015, "Privacy position".
        trackAnonymousUser: false,
        // Never hardcode: Datadog does not persist consent across navigation,
        // so whatever init gets wins every load. See ADR 20261015, "Privacy
        // position".
        trackingConsent,
        // Next.js Fast Refresh re-evaluates _app in development; without this the
        // SDK logs a console error on the second init attempt.
        silentMultipleInit: true,
        // Defaults to 20. SDK self-telemetry is pure overhead for a vitals-only
        // install.
        telemetrySampleRate: 0,
        // Drops every non-view event (the only way to keep RUM out of Rollbar's
        // lane) and strips query strings from the three URL fields the SDK
        // lets us modify. See ADR 20261015, "Decision Outcome" and "Privacy
        // position".
        beforeSend: (event) => {
          if (event.type !== 'view') {
            return false;
          }

          event.view.url = stripUrlQuery(event.view.url) ?? event.view.url;
          event.view.referrer = stripUrlQuery(event.view.referrer);

          const lcp = event.view.performance?.lcp;
          if (lcp?.resource_url) {
            lcp.resource_url = stripUrlQuery(lcp.resource_url);
          }

          return true;
        },
      });

      log(
        'initialised env=%s version=%s sampleRate=%d consent=%s',
        config.applicationEnvironment,
        config.applicationVersion,
        config.rumSessionSampleRate,
        trackingConsent
      );

      return datadogRum;
    })
    // A failed chunk download (real after a deploy) or a throwing `init`:
    // either way RUM goes quiet and the page is unaffected.
    .catch((error: unknown) => {
      log('failed to load or initialise %o', error);
      return undefined;
    });

  return rumReady;
};
